/**
 * <CheckpointConversation> — un checkpoint : Sam pose ses questions pendant le live, ou juste
 * avant (organism).
 *
 * L'ancien `SamConversationBase` (les checkpoints `starting_block`, `raise_hand`,
 * `silent_mode_10min`). Une conversation GUIDÉE : Sam parle, propose ses réponses, l'élève en
 * touche une. Pas de composeur, donc, sauf quand une réponse demande d'écrire (`textEntry`).
 * Ce que la feuille de Sam (`<SamSheet>`, pendant le live) et la page du démarrage
 * (`<StudentStartingBlockPage>`, avant) montrent :
 *  - l'en-tête : la tête de Sam, le pétale à gagner (`<CheckpointReward>`), et la croix, qui
 *    n'apparaît qu'au bout de 10 s (`canClose`, l'horloge est à l'écran) — sa place est gardée
 *    avant, pour que rien ne saute. Une conversation finie sans pétale gagné (Sam en panne, le
 *    serveur a clos à sa place) n'a plus rien à gagner : pas de pétale ;
 *  - le fil de la conversation (`days`), où Sam parle en bulle et où la réponse de l'élève reste
 *    écrite : l'ancien écran remplaçait la bulle à chaque tour, et la question d'avant
 *    disparaissait ;
 *  - les réponses proposées (`<CheckpointReplies>`), puis, au bout, l'avis (« C'était clair ? »)
 *    et, avant le live, « C'est parti » (`endAction`).
 * Pendant le live, pas de « Voir l'activité » (retiré le 19/10) : la feuille laisse déjà voir le haut
 * de l'activité, et au large l'activité est à côté.
 * `dateEntry` : une réponse demande la date du contrôle — le champ du calendrier, puis « Envoyer ».
 * `error` : Sam n'a rien à dire (le serveur n'a pas répondu, ou rien de lisible) ; il le dit, et
 * la croix est là tout de suite : jamais un écran vide sans sortie.
 * `loading` : la conversation s'ouvre et Sam prépare sa première phrase. L'en-tête est là, la croix
 * aussi (refermer abandonne l'ouverture), et à la place du fil et du pied, leur forme
 * (`<CheckpointConversationSkeleton>`, sous `<DelayedSkeleton>`) : sa bulle, ses réponses en cartes.
 *
 * Contrôlée : le fil, l'attente, les réponses, la saisie, le pétale, la croix, l'avis arrivent en
 * props.
 *
 * Composes DS pieces only — `<ConversationHeader>`, `<ConversationThread>`, `<CheckpointReplies>`,
 * `<CheckpointReward>`, `<MessageComposer>`, `<CorrectionFeedback>`, `<Button>`, `<ConversationClose>`.
 */
import React from "react"
import { KeyboardAvoidingView, Platform, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Button } from "../../atoms/Buttons/Button"
import { CheckpointReplies, type UICheckpointReply } from "../../molecules/CheckpointReplies"
import { CheckpointReward } from "../../molecules/CheckpointReward"
import {
    CONVERSATION_CLOSE_SIZE,
    CONVERSATION_HEADER_AVATAR_SIZE,
    ConversationClose,
    ConversationHeader,
} from "../../molecules/ConversationHeader"
import {
    CorrectionFeedback,
    type CorrectionFeedbackTexts,
    type UICorrectionFeedback,
} from "../../molecules/CorrectionFeedback"
import { DatePicker } from "../../molecules/DatePicker"
import { MessageComposer } from "../../molecules/MessageComposer"
import {
    CONVERSATION_MAX_WIDTH,
    ConversationThread,
    groupConversation,
    SAM_INTERLOCUTOR,
    type UIConversationDay,
} from "../ConversationThread"
import {
    type UICheckpointEndAction,
    type UICheckpointTextEntry,
} from "../../../ports/primary/ui/types/studentCheckpoint"
import { CheckpointConversationSkeleton } from "./CheckpointConversationSkeleton"

// L'écriture ouverte et la sortie : leurs formes vivent dans `ports/primary/ui/types` (socle du live).
export type { UICheckpointEndAction, UICheckpointTextEntry }

export type CheckpointConversationProps = {
    /** Le fil, sans jour ni heure (`groupConversation(messages, { dated: false })`). */
    days: UIConversationDay[]
    /** Sam écrit. Défaut `false`. */
    thinking?: boolean
    /** Les réponses que Sam propose à ce tour. */
    replies?: UICheckpointReply[]
    onReply?: (key: string) => void
    /** La réponse touchée : elle part, ou son geste est ouvert. */
    chosenReplyKey?: string | null
    /** Une réponse demande d'écrire : le composeur, sous les réponses. */
    textEntry?: UICheckpointTextEntry | null

    /** Les réponses déjà données : la barre du pétale avance. */
    answered: number
    /** Le pétale est gagné. Défaut `false`. */
    earned?: boolean

    /** La croix : au bout de 10 s, ou tout de suite sur une erreur. */
    canClose: boolean
    onClose: () => void

    /** La conversation est allée au bout : l'avis, et la sortie. Défaut `false`. */
    ended?: boolean
    feedback?: UICorrectionFeedback | null
    onFeedback?: (value: UICorrectionFeedback) => void
    feedbackSubmitting?: boolean
    /** La sortie, au bout — « C'est parti », avant le live. */
    endAction?: UICheckpointEndAction

    /** Une réponse demande une date (le contrôle qui arrive) : le champ, et « Envoyer ». */
    dateEntry?: UICheckpointDateEntry | null

    /** Sam n'a rien à montrer : il le dit, et la croix est là. Défaut `false`. */
    error?: boolean
    /**
     * La conversation s'ouvre : Sam prépare sa première phrase. Sa forme à la place du fil et du pied,
     * et la croix. Défaut `false`.
     */
    loading?: boolean
    /** Ce que Sam dit alors. Défaut : la phrase de la séance (« … reprendre ton activité »). */
    errorText?: string
    /**
     * Le pied dégage lui-même l'indicateur d'accueil. Défaut `true` (dans une feuille) ; une page
     * qui pose déjà sa zone sûre passe `false`.
     */
    bottomInset?: boolean
    testID?: string
}

const TEXTS = {
    thinking: "Sam réfléchit…",
    close: "Fermer la conversation",
    placeholder: "Ta réponse ici…",
    datePlaceholder: "Choisir la date",
    sendDate: "Envoyer",
    error: "Oups, un souci est survenu de notre côté. Tu peux fermer cette fenêtre et reprendre ton activité.",
    feedback: {
        question: "C'était clair ?",
        yes: "C'était clair",
        no: "Ce n'était pas clair",
    } satisfies CorrectionFeedbackTexts,
}

const errorDays = (text: string) =>
    groupConversation([{ id: "checkpoint-error", author: "other", text }], {
        dated: false,
    })
const ERROR_DAYS = errorDays(TEXTS.error)

/** Une date à choisir pour répondre, `YYYY-MM-DD` ; vide tant qu'elle n'est pas choisie. */
export type UICheckpointDateEntry = {
    value: string
    onChange: (date: string) => void
    onSend: () => void
    sending?: boolean
}

export function CheckpointConversation(props: CheckpointConversationProps) {
    const {
        answered,
        earned = false,
        canClose,
        onClose,
        ended = false,
        error = false,
        loading = false,
        bottomInset = true,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const bottomPadding = bottomInset ? insets.bottom : 0
    // Finie sans pétale gagné, la conversation n'a plus rien à faire gagner.
    const reward = !error && !(ended && !earned)

    const close =
        canClose || error || loading ? (
            <ConversationClose onPress={onClose} accessibilityLabel={TEXTS.close} />
        ) : (
            <View style={styles.closeRoom} />
        )

    const header = (
        <ConversationHeader
            bare
            name={SAM_INTERLOCUTOR.name}
            avatar={<ChampionAvatar name="sam" size={CONVERSATION_HEADER_AVATAR_SIZE} />}
            end={
                <View style={styles.end}>
                    {reward && <CheckpointReward answered={answered} earned={earned} />}
                    {close}
                </View>
            }
        />
    )

    return (
        <KeyboardAvoidingView
            // Une réponse à écrire ouvre le clavier : iOS a besoin qu'on lui cède la place.
            behavior={Platform.OS === "ios" ? "padding" : undefined}
            style={styles.root}
            testID={testID}
        >
            {header}

            {loading && !error ? (
                <DelayedSkeleton style={styles.root}>
                    <CheckpointConversationSkeleton bottomPadding={bottomPadding} />
                </DelayedSkeleton>
            ) : (
                <CheckpointConversationBody {...props} bottomPadding={bottomPadding} />
            )}
        </KeyboardAvoidingView>
    )
}

/** Le fil et le pied, une fois la conversation ouverte. */
function CheckpointConversationBody(props: CheckpointConversationProps & { bottomPadding: number }) {
    const {
        days,
        thinking = false,
        replies = [],
        onReply,
        chosenReplyKey,
        textEntry,
        ended = false,
        feedback = null,
        onFeedback,
        feedbackSubmitting,
        endAction,
        dateEntry,
        error = false,
        errorText,
        bottomPadding,
    } = props
    // En erreur, le pied ne propose rien ; sinon, l'échange en cours ou sa fin.
    const answering = !error && !ended
    const closing = !error && ended

    return (
        <>
            <ConversationThread
                days={error ? (errorText != null ? errorDays(errorText) : ERROR_DAYS) : days}
                interlocutor={SAM_INTERLOCUTOR}
                typingLabel={thinking && !error ? TEXTS.thinking : undefined}
            />

            <View style={styles.footer(bottomPadding)}>
                <View style={styles.column}>
                    {answering && onReply != null && (
                        <CheckpointReplies
                            replies={replies}
                            onSelect={onReply}
                            chosenKey={chosenReplyKey}
                            disabled={thinking}
                        />
                    )}
                    {answering && textEntry != null && (
                        <MessageComposer
                            value={textEntry.value}
                            onChangeText={textEntry.onChange}
                            onSend={textEntry.onSend}
                            sending={textEntry.sending}
                            placeholder={TEXTS.placeholder}
                            autoFocus
                        />
                    )}
                    {answering && dateEntry != null && (
                        <View style={styles.dateEntry}>
                            <DatePicker
                                value={dateEntry.value}
                                onChange={dateEntry.onChange}
                                placeholder={TEXTS.datePlaceholder}
                            />
                            <Button
                                label={TEXTS.sendDate}
                                onPress={dateEntry.onSend}
                                disabled={dateEntry.value === ""}
                                loading={dateEntry.sending}
                                fullWidth
                            />
                        </View>
                    )}
                    {closing && onFeedback != null && (
                        <CorrectionFeedback
                            value={feedback}
                            onChange={onFeedback}
                            submitting={feedbackSubmitting}
                            texts={TEXTS.feedback}
                        />
                    )}
                    {closing && endAction != null && (
                        <Button label={endAction.label} onPress={endAction.onPress} fullWidth />
                    )}
                </View>
            </View>
        </>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    end: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
    },
    /** La place de la croix, gardée avant qu'elle n'arrive : le pétale ne saute pas. */
    closeRoom: {
        width: CONVERSATION_CLOSE_SIZE,
        height: CONVERSATION_CLOSE_SIZE,
    },
    footer: (bottomInset: number) => ({
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["2"],
        paddingBottom: Math.max(bottomInset, theme.spacing["3"]),
    }),
    /** La colonne du fil : les réponses ne s'étirent pas plus large que les bulles. */
    column: {
        width: "100%",
        maxWidth: CONVERSATION_MAX_WIDTH,
        alignSelf: "center",
        gap: theme.spacing["3"],
    },
    dateEntry: {
        gap: theme.spacing["2"],
    },
}))
